import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiPayloadCreator } from "util/api";
import { optionsSlice } from "redux/features/optionData";
import { profileSlice } from "redux/features/profile";
import { selectAuthToken } from "redux/features/auth";

import { handleExternallyLoadedProfileResource } from "redux/features/profileResources";

/*
  Unsaved changes are stored in nested objects, whose keys are as indicated
  below. Branches are always removed when no unsaved changes remain, to
  make it easy for code modules to check if there are any unsaved changes
  in areas for which they are responsible.

   (Top-level key) id: id of person whose profile is being edited
   section:      name of the UI section responsible
   resourceType: type of resource on server (serves as the path to the
                 resource collection on the RESTful api endpont)
   resourceId:   id of resource, or null (NEW_RESOURCE) if new resource
                 is to be created

   Within each change record (nested object with above keys), there are
   entries:

        changes:  Object with key/value pairs representing new field values
        toDelete: Optional boolean indicating the object is to be deleted
        pending:  Field values from changes that are currently being sent
                  to the server, or null if no changes pending
        failed:   Object with error details, including at least an entry
                  named "message" with the error message, if an error occurred;
                  null if no error since last change or server communication.

*/

import {
  RESOURCE_TYPE_PERSON,
  RESOURCE_TYPE_EMAIL_ADDRESS,
  RESOURCE_TYPE_AFFILIATION_STATUS,
  RESOURCE_TYPE_PARTICIPATION,
  NEW_RESOURCE,
} from "redux/typeConstants";

/* After updating a resource, aspects of the person's profile may need updating,
   as may the list of people if the change affects the way people are displayed
   on the list. The following routine reloads the list of people:
*/

function updatePersonList(thunkAPI) {
  const personOptions = optionsSlice("people").selector(thunkAPI.getState());
  if (personOptions.loaded)
    thunkAPI.dispatch(optionsSlice("people").asyncActions.reload());
}

/* The following resource-type-specific update handlers deal with updates of
specific resource types. */

const handleUpdate = {};

handleUpdate[RESOURCE_TYPE_PERSON] = (success, arg, thunkAPI) => {
  updatePersonList(thunkAPI);
  thunkAPI.dispatch(profileSlice.actions.updatePerson(success));
};

handleUpdate[RESOURCE_TYPE_EMAIL_ADDRESS] = (success, arg, thunkAPI) => {
  const { resourceId, toDelete } = arg;

  const profile = profileSlice.selector(thunkAPI.getState()).data;
  const updatedEmail = { ...success };
  const allEmails = profile.email_addresses.filter(
    (email) =>
      email.id !== resourceId &&
      email.address.toUpperCase() !== updatedEmail.address.toUpperCase()
  );
  delete updatedEmail.set_as_primary;
  delete updatedEmail.reactivated_affiliation;
  delete updatedEmail.merged_profile;
  if (!toDelete) allEmails.push(updatedEmail);
  let primaryEmail = profile.primary_email_address;
  if (
    primaryEmail &&
    !allEmails.map((email) => email.id).includes(primaryEmail.id)
  ) {
    primaryEmail = null;
  }

  if (success.set_as_primary) primaryEmail = { ...updatedEmail };
  let affiliationSummary = profile.affiliation_summary;
  let affiliationStatuses = profile.affiliation_status_summary;

  if (success.reactivated_affiliation) {
    affiliationSummary = affiliationSummary.map((summary) =>
      summary.affiliation_id === success.reactivated_affiliation.id
        ? {
            ...summary,
            last_valid_on: null,
          }
        : summary
    );
    affiliationStatuses =
      success.reactivated_affiliation.updated_status_summary;
  }

  thunkAPI.dispatch(
    profileSlice.actions.updatePerson(
      success.merged_profile || {
        public_id: profile.public_id,
        email_addresses: allEmails,
        primary_email_address: primaryEmail,
        affiliation_summary: affiliationSummary,
        affiliation_status_summary: affiliationStatuses,
      }
    )
  );
};

handleUpdate[RESOURCE_TYPE_AFFILIATION_STATUS] = (success, arg, thunkAPI) => {
  const { id } = arg;
  thunkAPI.dispatch(
    profileSlice.actions.updatePerson({
      public_id: id,
      affiliation_summary: success.affiliation_summary,
      affiliation_status_summary: success.all_records,
      education_level_info: success.education_level_info,
    })
  );
};

// A participation is a separate resource but a summary of the participation
// is also present in the main profile. Update the summary when this
// information changes. The code for this is in profileResource.js.
handleUpdate[RESOURCE_TYPE_PARTICIPATION] = (success, arg, thunkAPI) => {
  const { id: personId, resourceType, resourceId } = arg;
  handleExternallyLoadedProfileResource({
    personId,
    resourceType,
    resourceId,
    data: success,
    dispatch: thunkAPI.dispatch,
  });
};

const apiCall = createAsyncThunk(
  "changes/apiCall",
  apiPayloadCreator("", "post", (success, arg, thunkAPI) => {
    const { resourceType } = arg;

    const handler = handleUpdate[resourceType];
    if (handler) handler(success, arg, thunkAPI);
    else console.error("Unrecognized resouce type " + resourceType);

    return success;
  })
);

// Return a change record (if passed a full list of id, section, resourceType,
// and resourceId) or a subtree of change records (if passed only an initial
// subset of those keys). Returns undefined if there is no such change record
// or subtree.
function getSubslice(state, ...sliceKeys) {
  let subSlice = state;
  sliceKeys.forEach((key) => {
    if (subSlice) subSlice = subSlice[key];
  });
  return subSlice;
}

// Return the change record for specified  id, section, resourceType, and
// resourceId, creating it (and parent objects) if necessary.
function getOrCreateChangeRecord(state, id, section, resourceType, resourceId) {
  let subSlice = state;
  [id, section, resourceType].forEach((key) => {
    if (!subSlice[key]) subSlice[key] = {};
    subSlice = subSlice[key];
  });
  if (!subSlice[resourceId])
    subSlice[resourceId] = {
      changes: {},
      pending: null,
      failed: null,
    };

  return subSlice[resourceId];
}

// Delete a change record and any subsequently empty ancestor subtree objects
// if the change record has no unsaved changes nor any pending changes.
function cleanChangeRecord(state, id, section, resourceType, resourceId) {
  const record = getSubslice(state, id, section, resourceType, resourceId);
  if (record && record.pending) return;
  if (record && Object.keys(record.changes).length > 0 && !record.toDelete)
    return;

  if (record) {
    delete record.changes;
    delete record.toDelete;
    delete record.pending;
    delete record.failed;
  }

  const sliceKeys = [id, section, resourceType, resourceId];
  while (sliceKeys.length > 0) {
    const subKey = sliceKeys.pop();
    const subSlice = getSubslice(state, ...sliceKeys);
    if (
      subSlice &&
      subSlice[subKey] &&
      Object.keys(subSlice[subKey]).length === 0
    )
      delete subSlice[subKey];
  }
}

export const changesSlice = createSlice({
  name: "changes",
  initialState: {},
  reducers: {
    setChanged: (state, action) => {
      const {
        id,
        section,
        resourceType,
        resourceId,
        field,
        value,
        toDelete = false,
      } = action.payload;

      const record = getOrCreateChangeRecord(
        state,
        id,
        section,
        resourceType,
        resourceId
      );
      let changed = null;
      if (toDelete) {
        changed = !record.toDelete;
        record.toDelete = true;
      } else {
        changed = record.changes[field] !== value;
        record.changes[field] = value;
      }
      if (changed) record.failed = null;
    },

    clearChanged: (state, action) => {
      const {
        id,
        section,
        resourceType,
        resourceId,
        field,
        toDelete,
      } = action.payload;
      const record = getSubslice(state, id, section, resourceType, resourceId);
      if (record) {
        let cleared = null;
        if (toDelete) {
          cleared = record.toDelete;
          delete record.toDelete;
        } else {
          cleared = record.changes[field] !== undefined;
          delete record.changes[field];
        }
        if (cleared) record.failed = null;
      }
      cleanChangeRecord(state, id, section, resourceType, resourceId);
    },

    deleteChangeRecord: (state, action) => {
      const { id, section, resourceType, resourceId } = action.payload;
      const record = getSubslice(state, id, section, resourceType, resourceId);
      if (record) {
        record.changes = {};
        delete record.toDelete;
        record.failed = null;
      }
      cleanChangeRecord(state, id, section, resourceType, resourceId);
    },
  },
  extraReducers: {
    [apiCall.pending]: (state, action) => {
      const { arg } = action.meta;
      const { id, section, resourceType, resourceId, data } = arg;
      const record = getOrCreateChangeRecord(
        state,
        id,
        section,
        resourceType,
        resourceId
      );
      record.failed = null;
      record.pending = { ...data };
    },

    [apiCall.fulfilled]: (state, action) => {
      const { arg } = action.meta;
      const { id, section, resourceType, resourceId, toDelete } = arg;
      const record = getOrCreateChangeRecord(
        state,
        id,
        section,
        resourceType,
        resourceId
      );
      record.failed = null;
      Object.entries(record.pending).forEach(([field, value]) => {
        if (
          record.changes[field] === value ||
          // If setting a compound value, treat it as fully set
          typeof record.changes[field] === "object"
        )
          delete record.changes[field];
      });
      // Parts of a resource may be edited in more than one section;
      // delete changes even if the values conflict, to cover the case where
      // the change is a transient action and the conflicting-value action is
      // no longer applicable otherwise.
      const root = getSubslice(state, id) || {};
      Object.keys(root)
        .filter((s) => s !== section)
        .forEach((otherSection) => {
          const otherRecord = getSubslice(
            state,
            id,
            otherSection,
            resourceType,
            resourceId
          ) || { changes: {} };
          Object.keys(record.pending).forEach((field) => {
            delete otherRecord.changes[field];
          });
          cleanChangeRecord(state, id, otherSection, resourceType, resourceId);
        });
      record.pending = null;
      if (toDelete) delete record.toDelete;
      cleanChangeRecord(state, id, section, resourceType, resourceId);
    },

    [apiCall.rejected]: (state, action) => {
      const { arg } = action.meta;
      const { id, section, resourceType, resourceId } = arg;
      const record = getOrCreateChangeRecord(
        state,
        id,
        section,
        resourceType,
        resourceId
      );
      record.pending = null;
      record.failed = action.payload || { message: "Unknown error" };
    },
  },
});

changesSlice.selector = (store) => store.changes;

changesSlice.asyncActions = {
  save: ({ id, section, resourceType, resourceId }) => (dispatch, getState) => {
    const isNew = resourceId === NEW_RESOURCE;
    const path = "/" + resourceType + (isNew ? "" : "/" + resourceId);
    const changeRecord = getSubslice(
      changesSlice.selector(getState()),
      id,
      section,
      resourceType,
      resourceId
    );
    if (changeRecord && !changeRecord.pending)
      dispatch(
        apiCall({
          id,
          path,
          section,
          resourceType,
          resourceId,
          method: isNew ? "post" : changeRecord.toDelete ? "delete" : "patch",
          selectAuthToken,
          data: changeRecord.toDelete ? undefined : changeRecord.changes,
        })
      );
  },
};

export default changesSlice.reducer;

export const changesSubslice = getSubslice;

export function updateChanges(
  dispatch,
  changeKeys,
  field,
  origValue,
  newValue,
  equalityFunc = (a, b) => a === b
) {
  if (equalityFunc(newValue, origValue))
    dispatch(
      changesSlice.actions.clearChanged({
        ...changeKeys,
        field,
      })
    );
  else
    dispatch(
      changesSlice.actions.setChanged({
        ...changeKeys,
        field,
        value: newValue,
      })
    );
}

export function clearChangedValue(dispatch, changeKeys, field) {
  dispatch(
    changesSlice.actions.clearChanged({
      ...changeKeys,
      field,
    })
  );
}
